import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { BulletList, NumberedSection, PRIVACY_EMAIL, Paragraph, Section, SubHeading, SummaryItem, policyLinkClass } from '../components/privacy/PolicyBlocks';
import { StatePrivacySections } from '../components/privacy/StatePrivacySections';
const CONTACT_SECTION = 15;
export function PrivacyPolicyPage() {
  usePageMeta('Privacy Policy | OnlineCollegeGuide', 'How OnlineCollegeGuide.io collects, uses, and shares personal information, and how residents of every U.S. state with a privacy law can exercise their rights.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-2347" data-mp="2924afzao9hli">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-2348" data-mp="292xf8uq2lx10">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-2349" data-mp="1v45zz4zh25ba">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2350" data-mp="29r3m8v3h6i3x" />
          Back to home
        </Link>

        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-2351" data-mp="24ssc0j99q5l6">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-2352" data-mp="24sa42pd8u2xf">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-2353" data-mp="2eqzho5y3x17v">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        <div className="space-y-5 leading-relaxed text-base" data-id="element-2354" data-mp="1m07ntf4ikonp">
          <p data-id="element-2355" data-mp="20rq7po0hv3cc">
            This privacy notice for OnlineCollegeGuide.io, operated by
            Ironsmith Media ("we," "us," or "our"), describes how and why we
            collect, store, use, and share your information when you use our
            services, such as when you:
          </p>
          <ul className="list-disc pl-6 space-y-2" data-id="element-2356" data-mp="13arjshtmee2j">
            <li data-id="element-2357" data-mp="1szojltzwo5ra">
              Visit our website, or any website of ours that links to this
              privacy notice
            </li>
            <li data-id="element-2358" data-mp="1x08hx0w7tff0">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p data-id="element-2359" data-mp="26r3fh1eww1fr">
            <strong className="text-navy" data-id="element-2360" data-mp="26qtll2qqxa4s">Questions or concerns?</strong>{' '}
            Reading this privacy notice will help you understand your privacy
            rights and choices. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </div>

        {/* The one action most readers came for, before the full notice. */}
        <aside aria-labelledby="privacy-quick-actions" className="mt-10 rounded-xl bg-gray-50 border border-gray-200 p-6" data-id="element-2361" data-mp="1o0lmqwu2x5ue">
          <h2 id="privacy-quick-actions" className="text-lg font-bold text-navy" data-id="element-2362" data-mp="2ug5us2m2pi73">
            Exercise your privacy rights
          </h2>
          <ul className="mt-3 space-y-2 text-gray-700 leading-relaxed" data-id="element-2363" data-mp="nsoqx4p38bq7">
            <li data-id="element-2364" data-mp="itlv8auzzhuw">
              <strong className="text-navy" data-id="element-2365" data-mp="it2cirxiwpft">Opt out of sale or targeted advertising:</strong>{' '}
              <Link to="/do-not-sell-my-info" className={policyLinkClass} data-id="element-2366" data-mp="e23wzukczvdm">
                Do Not Sell or Share My Personal Information
              </Link>
            </li>
            <li data-id="element-2367" data-mp="51xji8onuveg">
              <strong className="text-navy" data-id="element-2368" data-mp="52gs2ysf87ft">Access, correct, delete, or appeal:</strong>{' '}
              email{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className={policyLinkClass} data-id="element-2369" data-mp="1t2mhjubynp">
                {PRIVACY_EMAIL}
              </a>
            </li>
            <li data-id="element-2370" data-mp="3rb8alitpa99e">
              <strong className="text-navy" data-id="element-2371" data-mp="3ma2gphob0fya">State-by-state details:</strong>{' '}
              <a href="#section-10" className={policyLinkClass} data-id="element-2372" data-mp="3mc5bef0eix9c">
                Your U.S. State Privacy Rights
              </a>
            </li>
          </ul>
        </aside>

        <Section title="Summary of Key Points" data-id="element-2373" data-mp="1kj7f297f1mjn">
          <SummaryItem label="What personal information do we process?" text="The information you submit in our forms — name, contact details, location, and education interests — plus device and browsing information collected automatically." data-id="element-2374" data-mp="1sjn8gsyv838s" />
          <SummaryItem label="Do we process sensitive personal information?" text="We do not process sensitive personal information." data-id="element-2375" data-mp="1ink55jsa1yk9" />
          <SummaryItem label="Do we receive information from third parties?" text="We may receive technical and advertising data from third parties, including advertising platforms and analytics providers." data-id="element-2376" data-mp="19rb98f3idi2v" />
          <SummaryItem label="How do we process your information?" text="To match you with educational programs, communicate with you, improve our services, prevent fraud, comply with law, and with your consent where required." data-id="element-2377" data-mp="14tsycj5xkss8" />
          <SummaryItem label="Do we sell or share personal information?" text="Yes. We disclose the information you submit to educational institutions and marketing partners, and allow advertising partners to collect browsing data. You can opt out at any time." data-id="element-2378" data-mp="uutv5g3xppcm" />
          <SummaryItem label="How do we keep your information safe?" text="With organizational and technical measures, but no method is 100% secure." data-id="element-2379" data-mp="l9at1peafnm3" />
          <SummaryItem label="What are your rights?" text="Depending on your state, you may have rights to access, correct, delete, and port your data, opt out of sale, targeted advertising, and profiling, and appeal our decisions. We extend these rights to all U.S. visitors." data-id="element-2380" data-mp="v4m9qd99b4wh" />
          <SummaryItem label="How do you exercise your rights?" text="Through our Do Not Sell or Share page, a Global Privacy Control browser signal, email, or mail. See Section 10." data-id="element-2381" data-mp="zxp42nluyidq" />
        </Section>

        <Section title="Table of Contents" data-id="element-2382" data-mp="6kfnxfhkbq0o">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-2383" data-mp="6lh3nx9ix0oq">
            {tocItems.map(item => <li key={item.id} data-id="element-2384" data-mp="am0xaquw1m64">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-2385" data-mp="fkueat9dvvb1">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        <NumberedSection id="section-1" number={1} title="What Information Do We Collect?" data-id="element-2386" data-mp="pd02rt0ffulh">
          <SubHeading data-id="element-2387" data-mp="yatyb1qdak7l">Personal information you disclose to us</SubHeading>
          <Paragraph data-id="element-2388" data-mp="y9iqmqtswsb7">
            We collect personal information that you voluntarily provide to us
            when you express an interest in obtaining information about
            educational programs, when you participate in activities on the
            services, or otherwise when you contact us. This may include:
          </Paragraph>
          <BulletList items={['Names', 'Phone numbers', 'Email addresses', 'Mailing addresses and ZIP codes', 'Highest level of education and graduation year', 'Degree level, area of study, program preferences, and preferred start date']} data-id="element-2389" data-mp="2xjbz57u1pp8x" />

          <SubHeading data-id="element-2390" data-mp="3gh4v1q694ywt">Information automatically collected</SubHeading>
          <Paragraph data-id="element-2391" data-mp="3ggd4wuvfbtdx">
            We automatically collect certain information when you visit, use,
            or navigate the services, including:
          </Paragraph>
          <BulletList items={['IP address and approximate location derived from it', 'Browser and device characteristics, operating system, and language preferences', 'Referring URLs and campaign parameters', 'Pages viewed, time spent, and clickstream data', 'Cookie, pixel, and advertising identifiers', 'Records of your interaction with our forms, used to document consent']} data-id="element-2392" data-mp="36hno75f9kez9" />
        </NumberedSection>

        <NumberedSection id="section-2" number={2} title="How Do We Process Your Information?" data-id="element-2393" data-mp="3v7ckhdh9p4ex">
          <Paragraph data-id="element-2394" data-mp="3m53b4qxqjh34">
            We process your personal information for a variety of reasons,
            including:
          </Paragraph>
          <BulletList items={['To match you with educational programs and share your inquiry with institutions you are matched with', 'To respond to user inquiries and offer support', 'To send administrative information to you', 'To send you marketing and promotional communications, with your consent where required', 'To deliver targeted advertising to you', 'To document your consent and comply with telemarketing laws', 'To protect our services and investigate potential fraud or violations', 'To identify usage trends and measure the effectiveness of campaigns', "To save or protect an individual's vital interest", 'To comply with our legal obligations']} data-id="element-2395" data-mp="dquu8g4kseqs" />

          <SubHeading data-id="element-2396" data-mp="1fko1m53lqz98">Email communications</SubHeading>
          <Paragraph data-id="element-2397" data-mp="1flz8wew9ap53">
            When you submit a form on our website, you may consent to receive
            marketing and promotional emails from OnlineCollegeGuide.io and its
            educational partners at the email address you provide. Marketing
            emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-2398" data-mp="11u2g08mrkb7n">{PRIVACY_EMAIL}</span>.
            Every marketing email includes an unsubscribe link. You may opt out
            at any time and your request will be honored within 10 business
            days. Opting out of marketing email does not affect transactional
            or administrative messages related to a specific inquiry you have
            submitted.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-3" number={3} title="SMS / Text Message Communications" data-id="element-2399" data-mp="1bklvvohcxor2">
          <SubHeading data-id="element-2400" data-mp="16ske5n1f75tn">How we collect SMS consent</SubHeading>
          <Paragraph data-id="element-2401" data-mp="11uskanh7t9mt">
            When you submit a form on our website, you may expressly consent to
            receive autodialed and/or pre-recorded marketing text messages
            (SMS/MMS) from OnlineCollegeGuide.io and its educational partners at
            the mobile phone number you provide. This consent is obtained at the
            point of form submission and is documented with the date, time, IP
            address, and page URL at which consent was given.
          </Paragraph>

          <SubHeading data-id="element-2402" data-mp="2i8iodu8c3xwg">How we use your phone number for SMS</SubHeading>
          <Paragraph data-id="element-2403" data-mp="2i6odl8qs1m63">
            If you have provided consent, we may send you text messages for
            purposes including:
          </Paragraph>
          <BulletList items={['Information about educational programs matching your interests', 'Follow-up communications related to your inquiry', 'Promotional offers and marketing messages from us or our educational partners']} data-id="element-2404" data-mp="2lrhre9p4z464" />

          <SubHeading data-id="element-2405" data-mp="2zgs11j2dle5i">Consent is not required</SubHeading>
          <Paragraph data-id="element-2406" data-mp="22s3vvnbrdldi">
            Your consent to receive SMS/MMS messages is never a condition of
            using our services, receiving information, or enrolling in any
            program. You may choose not to provide consent and still access our
            website and resources.
          </Paragraph>

          <SubHeading data-id="element-2407" data-mp="2gs4yb77o90v3">Message frequency and rates</SubHeading>
          <Paragraph data-id="element-2408" data-mp="2gpiqd6gvcjcc">
            Message frequency varies based on your interactions and
            preferences. Standard message and data rates may apply depending on
            your carrier and plan.
          </Paragraph>

          <SubHeading data-id="element-2409" data-mp="32id1z19cdbr0">How to opt out</SubHeading>
          <Paragraph data-id="element-2410" data-mp="32h1yl2jt660f">
            You may revoke your consent and opt out of SMS communications at
            any time by:
          </Paragraph>
          <BulletList items={['Replying STOP to any text message you receive from us', `Contacting us directly using the information in Section ${CONTACT_SECTION}`]} data-id="element-2411" data-mp="3cgj33gnbqdut" />
          <Paragraph data-id="element-2412" data-mp="2jt6pvx31zie6">
            Opt-out requests will be honored promptly. After opting out, you
            may receive a single confirmation message. You will not receive
            further SMS marketing messages unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-2413" data-mp="2oqoqn06x2rb3">For assistance</SubHeading>
          <Paragraph data-id="element-2414" data-mp="2opx5kjay4bz2">
            Reply HELP to any text message for support information, or contact
            us directly using the information in Section {CONTACT_SECTION}.
          </Paragraph>

          <SubHeading data-id="element-2415" data-mp="1w2arui807qyh">Third-party SMS senders</SubHeading>
          <Paragraph data-id="element-2416" data-mp="bk8lyimjn88i">
            When you consent to receive communications from our educational
            partners, those partners may contact you directly via SMS under
            their own messaging programs and privacy policies. We encourage you
            to review the privacy policies of any educational institution or
            partner that contacts you.
          </Paragraph>

          <SubHeading data-id="element-2417" data-mp="uai0mrziosh3">Record retention</SubHeading>
          <Paragraph data-id="element-2418" data-mp="ubszcde3va1c">
            We retain records of SMS consent, including timestamp, IP address,
            form URL, and submitted form data, in accordance with the retention
            practices described in Section 6.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-4" number={4} title="When and With Whom Do We Share Your Personal Information?" data-id="element-2419" data-mp="paltijviwkxb">
          <Paragraph data-id="element-2420" data-mp="3a1w3anmgdroe">
            We may share information in the following situations:
          </Paragraph>
          <ul className="space-y-3 mt-4" data-id="element-2421" data-mp="3q9shv4qr97r2">
            {sharingItems.map(item => <li key={item.label} className="flex gap-3 leading-relaxed text-gray-700" data-id="element-2422" data-mp="3litytxedtvk4">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-navy flex-shrink-0" data-id="element-2423" data-mp="3gkilvdssj4xo" />
                <span data-id="element-2424" data-mp="3blfirywdf8sc">
                  <strong className="text-navy" data-id="element-2425" data-mp="3blz3tohbidal">{item.label}:</strong>{' '}
                  {item.text}
                </span>
              </li>)}
          </ul>
          <Paragraph data-id="element-2426" data-mp="83e1kz50klpi">
            Some of these disclosures are considered a "sale" or "sharing" of
            personal information, or its use for "targeted advertising," under
            state privacy laws. You can opt out as described in Section 10.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-5" number={5} title="Do We Use Cookies and Other Tracking Technologies?" data-id="element-2427" data-mp="2nvuxh9cqvzfw">
          <Paragraph data-id="element-2428" data-mp="2jvkrvxsv7b1k">
            Yes. We use cookies, pixels, and similar technologies, including
            those provided by advertising and analytics partners and by
            services that record form interactions to document consent. See our{' '}
            <Link to="/cookie-policy" className={policyLinkClass} data-id="element-2429" data-mp="18b2fum2xtmc6">
              Cookie Policy
            </Link>{' '}
            for details, and Section 13 for how we honor opt-out preference
            signals.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-6" number={6} title="How Long Do We Keep Your Information?" data-id="element-2430" data-mp="1n0i51xmtljpr">
          <Paragraph data-id="element-2431" data-mp="1r3n4tsfin7ap">
            We keep your information only as long as necessary to fulfill the
            purposes outlined in this privacy notice, unless a longer period is
            required by law. Retention periods by category are listed in
            Section 11. When we have no ongoing legitimate business need to
            process your personal information, we will delete or de-identify
            it.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-7" number={7} title="How Do We Keep Your Information Safe?" data-id="element-2432" data-mp="zzrlnvw6mog3">
          <Paragraph data-id="element-2433" data-mp="1e0kk2rplu3oh">
            We have implemented reasonable administrative, technical, and
            physical security measures designed to protect the personal
            information we process. However, no electronic transmission over
            the internet or information storage technology can be guaranteed
            to be 100% secure.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-8" number={8} title="Do We Collect Information From Minors?" data-id="element-2434" data-mp="1woz7l50pqzkj">
          <Paragraph data-id="element-2435" data-mp="26nol6llktxuz">
            Our services are intended for adults. We do not knowingly collect
            personal information from, sell or share the personal information
            of, or market to anyone under 18 years of age. If you become aware
            that a minor has provided us with personal information, please
            contact us, and we will delete it.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id="section-9" number={9} title="What Are Your Privacy Choices?" data-id="element-2436" data-mp="2bczav6oeuki1">
          <Paragraph data-id="element-2437" data-mp="2g9pto1rlnu75">
            Every visitor, wherever they live, can make the following choices:
          </Paragraph>
          <BulletList items={['Opt out of marketing email using the unsubscribe link in any email, or by contacting us. Requests are processed within 10 business days.', 'Opt out of text messages by replying STOP to any message.', 'Opt out of the sale of your personal information and its use for targeted advertising through our Do Not Sell or Share page or a Global Privacy Control signal.', 'Request access to, correction of, or deletion of your personal information by contacting us.']} data-id="element-2438" data-mp="22i1gdv5m6zol" />
          <Paragraph data-id="element-2439" data-mp="241cry41e0lth">
            Residents of states with consumer privacy laws have the additional
            rights, request process, and appeal procedure described in
            Section 10.
          </Paragraph>
        </NumberedSection>

        <StatePrivacySections contactSection={CONTACT_SECTION} data-id="element-2440" data-mp="1p5deedvidtgy" />

        <NumberedSection id="section-14" number={14} title="Do We Make Updates to This Notice?" data-id="element-2441" data-mp="1p77yxr2vbjki">
          <Paragraph data-id="element-2442" data-mp="1gc0f8u6upvmf">
            Yes. We review this notice at least once every 12 months and update
            it as necessary to stay compliant with applicable laws. The updated
            version will be indicated by an updated "Last updated" date at the
            top of this notice. If we make material changes to how we process
            personal information, we will notify you as required by law.
          </Paragraph>
        </NumberedSection>

        <NumberedSection id={`section-${CONTACT_SECTION}`} number={CONTACT_SECTION} title="How Can You Contact Us About This Notice?" data-id="element-2443" data-mp="xg1uiyg46zpf">
          <Paragraph data-id="element-2444" data-mp="3d11pnihc7gqn">
            If you have questions about this notice or want to exercise your
            privacy rights, contact us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-2445" data-mp="3n12cheef0ezl">
            <p className="font-bold text-navy text-lg" data-id="element-2446" data-mp="2pdbseg4a4fmi">Ironsmith Media</p>
            <p className="text-gray-700 mt-2" data-id="element-2447" data-mp="2pb7d720xju6x">
              Attn: Privacy, 1612 UPAS Street, San Diego, CA 92103
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2448" data-mp="2zapvle16z7wv">
              Email:{' '}
              <a href={`mailto:${PRIVACY_EMAIL}`} className={policyLinkClass} data-id="element-2449" data-mp="2z9er1bzx9w4p">
                {PRIVACY_EMAIL}
              </a>
            </p>
            <p className="text-gray-700 mt-2" data-id="element-2450" data-mp="26liy8idh4od7">
              Opt-out requests:{' '}
              <Link to="/do-not-sell-my-info" className={policyLinkClass} data-id="element-2451" data-mp="2bl3ugl19eim8">
                Do Not Sell or Share My Personal Information
              </Link>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
const sharingItems = [{
  label: 'Educational institutions and marketing partners',
  text: 'When you request to be matched, we disclose the information you submit to schools and education marketing partners so they can contact you about programs. We may receive compensation for these disclosures.'
}, {
  label: 'Advertising partners',
  text: 'Advertising platforms may collect device and browsing information through cookies and pixels on our site to measure campaigns and show you ads elsewhere.'
}, {
  label: 'Service providers',
  text: 'Vendors who host our website, provide analytics, verify and record consent, and otherwise perform services for us under contracts that limit their use of your information.'
}, {
  label: 'Business transfers',
  text: 'In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.'
}, {
  label: 'Legal requirements',
  text: 'When required by law, or to protect rights, safety, and property.'
}];
const tocItems = [{
  id: 'section-1',
  label: 'What information do we collect?'
}, {
  id: 'section-2',
  label: 'How do we process your information?'
}, {
  id: 'section-3',
  label: 'SMS / text message communications'
}, {
  id: 'section-4',
  label: 'When and with whom do we share your personal information?'
}, {
  id: 'section-5',
  label: 'Do we use cookies and other tracking technologies?'
}, {
  id: 'section-6',
  label: 'How long do we keep your information?'
}, {
  id: 'section-7',
  label: 'How do we keep your information safe?'
}, {
  id: 'section-8',
  label: 'Do we collect information from minors?'
}, {
  id: 'section-9',
  label: 'What are your privacy choices?'
}, {
  id: 'section-10',
  label: 'Your U.S. state privacy rights'
}, {
  id: 'section-11',
  label: 'Additional information for California residents'
}, {
  id: 'section-12',
  label: 'Additional information for Nevada residents'
}, {
  id: 'section-13',
  label: 'Opt-out preference signals and Do-Not-Track'
}, {
  id: 'section-14',
  label: 'Do we make updates to this notice?'
}, {
  id: 'section-15',
  label: 'How can you contact us about this notice?'
}];